import mapboxgl from "mapbox-gl";
import api from "../apis/sharedInterceptors";
import { redirect } from "react-router-dom";
import { persistor } from "../redux/store/store";
import { resetCredentials } from "../redux/reducers/authReducer";
import i18n from "../i18n/i18n"; 
let authCheckPromise = null;
// Languages supported by Mapbox streets vector tiles
const MAPBOX_SUPPORTED_LANGS = ["ar", "de", "en", "es", "fr", "it", "ja", "ko", "pt", "ru", "vi", "zh-Hans", "zh-Hant"];

export const applyMapLanguage = (map, lang) => {
  if (!map || !map.getStyle()) return;

  const base = (lang || "en").split("-")[0];
  const code = MAPBOX_SUPPORTED_LANGS.includes(lang)
    ? lang
    : MAPBOX_SUPPORTED_LANGS.includes(base)
      ? base
      : "en";

  const field = ["coalesce", ["get", `name_${code}`], ["get", "name"]];

  map.getStyle().layers.forEach((layer) => {
    // Only touch symbol layers that already display a name
    if (layer.type !== "symbol") return;
    // Skip your own custom layers (DFCI names, icons, etc.)
    if (layer.source !== "composite") return;

    const textField = layer.layout?.["text-field"];
    if (!textField) return;

    // Only patch layers whose text-field references "name"
    const usesName = JSON.stringify(textField).includes("name");
    if (!usesName) return;

    map.setLayoutProperty(layer.id, "text-field", field);
  });
};
export const initializeMap = async (
  mapRef,
  mapContainerRef,
  selectedStyle,
  keyboard,
  store,
  setMoveEvent,
  onReady,
) => {
  try {
  if (!authCheckPromise) {
      authCheckPromise = api
        .get(`${process.env.REACT_APP_AUTH_BASEURL_V1}/auth/check/`)
        .finally(() => {
          authCheckPromise = null; // reset for next time
        });
    }
    await authCheckPromise;    const protectedTileServices = [
      process.env.REACT_APP_MAPPER_VEGETATION_BASEURL_V1,
      process.env.REACT_APP_MAPPER_WEATHER_BASEURL_V1,
      process.env.REACT_APP_MAPPER_DFCI_BASEURL_V1,
    ];

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: selectedStyle,
      center: [0, 0],
      zoom: 2,
      keyboard,
      prefetchZoomDelta: 0,
      projection: "globe",
      transformRequest: (url, resourceType) => {
        if (resourceType === "Tile") {
          const isProtected = protectedTileServices.some(
            (base) => base && url.startsWith(`${base}/`),
          );

          if (isProtected) {
            return {
              url,
              headers: {
                Authorization: `Bearer ${store.getState().auth.accessToken}`,
              },
            };
          }
        }
        return { url };
      },
    });

    mapRef.current.on("style.load", () => {
      const isNight = new Date().getHours() >= 18 || new Date().getHours() < 6;
      mapRef.current.setFog(
        isNight
          ? {
              color: "rgb(186, 210, 235)",
              "high-color": "rgb(159, 199, 211)",
              "horizon-blend": 0.015,
              "space-color": "rgb(17, 17, 32)",
              "star-intensity": 0.5,
            }
          : {
              range: [1, 10],
              color: "rgb(135, 206, 235)",
              "space-color": "rgb(33, 73, 92)",
              "star-intensity": 0,
            },
      );
    });

    mapRef.current.on("mousemove", (e) => {
      setMoveEvent(e);
    });

    mapRef.current.on("load", () => {
      // Setup geolocation control
      setupGeolocationControl(mapRef);

      // Add a small delay to ensure the control is fully initialized
      setTimeout(() => {
        if (mapRef.current?.geolocateControl) {
          mapRef.current.geolocateControl.trigger();
        }
      }, 1000); // 1 second delay

      // Call this only when everything is ready
      if (typeof onReady === "function") {
        onReady();
      }
    });
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn("Auth check failed - logging out");
      store.dispatch(resetCredentials());
      await persistor.purge();
      localStorage.clear();
      window.location.href = "/";
    } else {
      console.error("Map initialization failed:", error);
      // Optionally redirect to error page or show message
    }
  }
};

// Separate function to setup geolocation control
const setupGeolocationControl = (mapRef) => {
  // Remove existing geolocation control if it exists
  if (mapRef.current.geolocateControl) {
    try {
      mapRef.current.removeControl(mapRef.current.geolocateControl);
    } catch (e) {
      //console.log("Geolocation control not found or already removed");
    }
  }

  // Create new geolocation control
  const geolocate = new mapboxgl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: true,
    showUserHeading: true,
    showAccuracyCircle: true,
  });

  // Add to map and store reference
  mapRef.current.addControl(geolocate);
  mapRef.current.geolocateControl = geolocate;

  // Set up event listeners
  geolocate.once("geolocate", (position) => {
    const { longitude, latitude } = position.coords;
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: 12,
      speed: 0.5,
      curve: 1.2,
      essential: true,
    });
  });

  // // Handle tracking toggle events
  // geolocate.on("trackuserlocationstart", () => {
  //   console.log("Location tracking started");
  // });

  // geolocate.on("trackuserlocationend", () => {
  //   console.log("Location tracking ended");
  // });

  // // Handle errors
  // geolocate.on("error", (error) => {
  //   console.error("Geolocation error:", error);
  // });
};
export const addMapControls = (mapRef) => {
  const map = mapRef.current;
  if (map._controlsAdded) return;
  const observer = new MutationObserver(() => {
    document.querySelectorAll("[title]").forEach((el) => {
      el.removeAttribute("title");
    });
  });

  // Observe changes in the map container
  observer.observe(document.querySelector(".mapboxgl-map"), {
    childList: true,
    subtree: true,
  });

  // Add Navigation Control
  const navControl = new mapboxgl.NavigationControl();
  map.addControl(navControl);

 map.addControl(
  new mapboxgl.FullscreenControl({
    container: document.querySelector(".app-view"),
  }),
);

  // Add Scale Control
  const scaleControl = new mapboxgl.ScaleControl({
    maxWidth: 80,
    unit: "metric",
  });
  map.addControl(scaleControl, "bottom-right");
  map._controlsAdded = true;
  setTimeout(() => {
    addDataTestIdsToControls();
  }, 100);
  // Remove default tooltips (title attributes)
  const removeDefaultTooltips = () => {
    const controls = document.querySelectorAll(".mapboxgl-ctrl");
    controls.forEach((control) => {
      control.removeAttribute("title"); // This removes the default tooltips from controls
    });
  };

  setTimeout(removeDefaultTooltips, 100); // Run after map controls are added
};
const addDataTestIdsToControls = () => {
  // Navigation controls
  const zoomInButton = document.querySelector(".mapboxgl-ctrl-zoom-in");
  if (zoomInButton) {
    zoomInButton.setAttribute("data-testid", "zoom-in-button");
  }

  const zoomOutButton = document.querySelector(".mapboxgl-ctrl-zoom-out");
  if (zoomOutButton) {
    zoomOutButton.setAttribute("data-testid", "zoom-out-button");
  }

  const compassButton = document.querySelector(".mapboxgl-ctrl-compass");
  if (compassButton) {
    compassButton.setAttribute("data-testid", "compass-button");
  }

  // Fullscreen control
  const fullscreenButton = document.querySelector(".mapboxgl-ctrl-fullscreen");
  if (fullscreenButton) {
    fullscreenButton.setAttribute("data-testid", "fullscreen-button");
  }

  // Geolocation control
  const geolocateButton = document.querySelector(".mapboxgl-ctrl-geolocate");
  if (geolocateButton) {
    geolocateButton.setAttribute("data-testid", "geolocate-button");
  }

  // Scale control
  const scaleControl = document.querySelector(".mapboxgl-ctrl-scale");
  if (scaleControl) {
    scaleControl.setAttribute("data-testid", "scale-control");
  }
};
export const renderDrawToolTooltips = () => {
  // Custom tooltip for the Point button
  const pointButton = document.querySelector(".mapbox-gl-draw_point");
  if (pointButton) {
    pointButton.setAttribute("data-tooltip", i18n.t("mapControls.drawPoint"));
  }

  // Custom tooltip for the Line button
  const lineButton = document.querySelector(".mapbox-gl-draw_line");
  if (lineButton) {
    lineButton.setAttribute("data-tooltip", i18n.t("mapControls.drawLine"));
  }

  // Custom tooltip for the Polygon button
  const polygonButton = document.querySelector(".mapbox-gl-draw_polygon");
  if (polygonButton) {
    polygonButton.setAttribute("data-tooltip", i18n.t("mapControls.drawPolygon"));
  }
};
export const renderCustomTooltips = () => {
  // Custom tooltip for zoom in
  const zoomInButton = document.querySelector(".mapboxgl-ctrl-zoom-in");
  if (zoomInButton) {
    zoomInButton.setAttribute("data-tooltip", i18n.t("mapControls.zoomIn"));
  }

  // Custom tooltip for zoom out
  const zoomOutButton = document.querySelector(".mapboxgl-ctrl-zoom-out");
  if (zoomOutButton) {
    zoomOutButton.setAttribute("data-tooltip", i18n.t("mapControls.zoomOut"));
  }

  // Custom tooltip for North (Compass)
  const northButton = document.querySelector(".mapboxgl-ctrl-compass");
  if (northButton) {
    northButton.setAttribute("data-tooltip", i18n.t("mapControls.compass"));
  }

  // Custom tooltip for Full Screen
  const fullScreenButton = document.querySelector(".mapboxgl-ctrl-fullscreen");
  if (fullScreenButton) {
    fullScreenButton.setAttribute("data-tooltip", i18n.t("mapControls.fullscreen"));
  }

  // Custom tooltip for User Location (Geolocate control)
  const geolocateButton = document.querySelector(".mapboxgl-ctrl-geolocate");
  if (geolocateButton) {
    geolocateButton.setAttribute("data-tooltip", i18n.t("mapControls.geolocate"));
  } else {
    // If the button is not found, try checking later (wait for initialization)
    setTimeout(() => {
      const geolocateButton = document.querySelector(
        ".mapboxgl-ctrl-geolocate",
      );
      if (geolocateButton) {
        geolocateButton.setAttribute("data-tooltip", i18n.t("mapControls.geolocate"));
      }
    }, 500); // Retry after 500ms or adjust as necessary
  }
};
export const apply3DTerrain = (map) => {
  if (!map.getSource("mapbox-dem")) {
    map.addSource("mapbox-dem", {
      type: "raster-dem",
      url: "mapbox://mapbox.terrain-rgb",
      tileSize: 512,
      maxzoom: 14,
    });
  }
  map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });
};
export const remove3DTerrain = (map) => {
  if (map.getSource("mapbox-dem")) {
    map.removeSource("mapbox-dem");
    map.setTerrain(null);
  }
};

export const apply3DBuildings = (map) => {
  if (!map.getLayer("3d-buildings")) {
    map.addLayer({
      id: "3d-buildings",
      source: "composite",
      "source-layer": "building",
      filter: ["==", "extrude", "true"],
      type: "fill-extrusion",
      minzoom: 15,
      paint: {
        "fill-extrusion-color": "#aaa",
        "fill-extrusion-height": [
          "interpolate",
          ["linear"],
          ["zoom"],
          15,
          0,
          16.05,
          ["get", "height"],
        ],

        "fill-extrusion-base": [
          "interpolate",
          ["linear"],
          ["zoom"],
          15,
          0,
          16.05,
          ["get", "min_height"],
        ],
        "fill-extrusion-opacity": 0.8,
      },
    });
  }
};
export const remove3DBuildings = (map) => {
  if (map.getLayer("3d-buildings")) {
    map.removeLayer("3d-buildings");
  }
};
